import { useState, useRef, type ChangeEvent, type DragEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import StatusBar from "../components/StatusBar";
import { parseEmailPdf } from "../lib/email/extractPdf";
import { scanThread, redactionCount } from "../lib/email/piiScan";
import { ingestThread } from "../lib/email/ingest";
import type { ParsedEmailThread, EmailMessage } from "../lib/email/types";

/**
 * UploadEvidence — the Eater's evidence-ingest surface (email PDFs, v0).
 * Multi-file parse + PII scan + redact toggle + draft->approve + batch commit.
 * Reached as /upload-evidence?case=<caseId>; commit binds to that case.
 */

interface FileResult {
  file: string;
  fileObj?: File;       // retained for SHA-256 at commit
  thread?: ParsedEmailThread;
  flagCount?: number;
  error?: string;
}
interface CommitOutcome { file: string; job_id?: string; error?: string; }

type Flow =
  | { kind: "idle" }
  | { kind: "selected"; files: File[] }
  | { kind: "parsing"; index: number; total: number; current: string; phase: string }
  | { kind: "review"; results: FileResult[] }
  | { kind: "committing"; index: number; total: number; current: string }
  | { kind: "committed"; outcomes: CommitOutcome[] }
  | { kind: "error"; message: string };

const PHASE_LABEL: Record<string, string> = {
  loading: "loading", "extracting-text": "reading text", "ocr-header": "OCR header",
  "vision-fallback": "vision fallback", parsing: "splitting thread", done: "done",
};

function UploadEvidence() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caseId = params.get("case");
  const [state, setState] = useState<Flow>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function pick(fileList: FileList | null) {
    if (!fileList) return;
    const pdfs = Array.from(fileList).filter((f) => f.type === "application/pdf" && f.size <= 52 * 1024 * 1024);
    if (!pdfs.length) { setState({ kind: "error", message: "No valid PDFs selected (PDF only, max 50 MB each)." }); return; }
    setState({ kind: "selected", files: pdfs });
  }
  function onFileInput(e: ChangeEvent<HTMLInputElement>) { pick(e.target.files); }
  function onDrop(e: DragEvent<HTMLDivElement>) { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files); }

  async function runBatch(files: File[]) {
    const done: FileResult[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;
      setState({ kind: "parsing", index: i, total: files.length, current: file.name, phase: "loading" });
      try {
        const thread = await parseEmailPdf(file, {
          onProgress: (phase) => setState({ kind: "parsing", index: i, total: files.length, current: file.name, phase }),
        });
        const scanned = scanThread(thread.messages);
        thread.messages = scanned.messages;
        done.push({ file: file.name, fileObj: file, thread, flagCount: scanned.flagCount });
      } catch (e) {
        done.push({ file: file.name, error: e instanceof Error ? e.message : String(e) });
      }
    }
    setState({ kind: "review", results: done });
  }

  // immutably toggle a single flag's redact bit
  function toggleRedact(fileIdx: number, msgIdx: number, flagIdx: number) {
    setState((prev) => {
      if (prev.kind !== "review") return prev;
      const results = prev.results.map((r, ri) => {
        if (ri !== fileIdx || !r.thread) return r;
        const messages = r.thread.messages.map((m, mi) => {
          if (mi !== msgIdx) return m;
          const pii_flags = m.pii_flags.map((f, fi) => (fi === flagIdx ? { ...f, redact: !f.redact } : f));
          return { ...m, pii_flags };
        });
        return { ...r, thread: { ...r.thread, messages } };
      });
      return { kind: "review", results };
    });
  }

  async function runCommit(results: FileResult[]) {
    if (!caseId) { setState({ kind: "error", message: "No case bound — open this from a case's Hell Hounds panel." }); return; }
    const ok = results.filter((r) => r.thread && r.fileObj);
    const outcomes: CommitOutcome[] = [];
    for (let i = 0; i < ok.length; i++) {
      const r = ok[i];
      if (!r || !r.thread || !r.fileObj) continue;
      setState({ kind: "committing", index: i, total: ok.length, current: r.file });
      try {
        const res = await ingestThread(caseId, r.thread, r.fileObj);
        outcomes.push({ file: r.file, job_id: res.job_id });
      } catch (e) {
        outcomes.push({ file: r.file, error: e instanceof Error ? e.message : String(e) });
      }
    }
    setState({ kind: "committed", outcomes });
  }

  function reset() { setShowFull(false); setState({ kind: "idle" }); }

  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant="modes" overlay="heavy" />
      <StatusBar />
      <main className="relative z-10 mx-auto max-w-5xl px-6 py-12">
        <header className="mb-8">
          <button type="button" onClick={() => navigate(caseId ? `/case/${caseId}/hounds` : "/")} className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute hover:text-bone">
            ← back
          </button>
          <p className="mt-6 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// ingestion // the eater // evidence</p>
          <h1 className="mt-2 font-display text-3xl font-light leading-tight tracking-tight text-bone md:text-4xl" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.7)" }}>
            Feed it the correspondence.
          </h1>
          <p className="mt-3 max-w-2xl font-display text-sm leading-relaxed text-bone-dim">
            Drop a batch of email threads exported to PDF (iOS Mail, Gmail, Outlook). The Eater parses every
            message, recovers real timestamps, and flags incidental sensitive data. Click a flag to redact it.
            Nothing writes to the corpus until you commit.
          </p>
          {!caseId && (state.kind === "review") && (
            <p className="mt-3 font-display text-base font-light uppercase tracking-[0.14em] text-amber-300">⚠ no case bound — reopen from a case's Hell Hounds panel to commit</p>
          )}
        </header>

        {(state.kind === "idle" || state.kind === "selected") && (
          <DropZone files={state.kind === "selected" ? state.files : []} dragOver={dragOver}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
            onDrop={onDrop} onClick={() => fileInputRef.current?.click()}
            onParse={() => state.kind === "selected" && runBatch(state.files)} onClear={reset} />
        )}

        {state.kind === "parsing" && <Busy label={`the eater // ${state.index + 1} of ${state.total} // ${PHASE_LABEL[state.phase] ?? state.phase}`} current={state.current} total={state.total} index={state.index} />}
        {state.kind === "committing" && <Busy label={`committing // ${state.index + 1} of ${state.total}`} current={state.current} total={state.total} index={state.index} />}

        {state.kind === "review" && (
          <ReviewPanel results={state.results} showFull={showFull} canCommit={!!caseId}
            onToggleView={() => setShowFull((v) => !v)} onToggleRedact={toggleRedact}
            onCommit={() => runCommit(state.results)} onCancel={reset} />
        )}

        {state.kind === "committed" && <CommittedPanel outcomes={state.outcomes} caseId={caseId} onDone={() => navigate(caseId ? `/case/${caseId}/hounds` : "/")} onMore={reset} />}

        {state.kind === "error" && <ErrorPanel message={state.message} onReset={reset} />}

        <input ref={fileInputRef} type="file" accept="application/pdf" multiple className="hidden" onChange={onFileInput} />
      </main>
    </div>
  );
}

function DropZone(props: {
  files: File[]; dragOver: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void; onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void; onClick: () => void; onParse: () => void; onClear: () => void;
}) {
  const { files, dragOver } = props;
  const has = files.length > 0;
  return (
    <div className="space-y-4">
      <div onDragOver={props.onDragOver} onDragLeave={props.onDragLeave} onDrop={props.onDrop} onClick={has ? undefined : props.onClick}
        className={`group relative cursor-pointer rounded border-2 border-dashed px-8 py-16 text-center backdrop-blur-sm transition-all ${
          dragOver ? "border-ember-bright/80 bg-ember/20 shadow-ember-glow" : has ? "border-ember-bright/40 bg-iron/70" : "border-steel-dark bg-iron/50 hover:border-ember-bright/40 hover:bg-iron/70"}`}>
        {!has ? (
          <>
            <p className="font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// drop zone</p>
            <p className="mt-4 font-display text-2xl font-light text-bone">Drop email PDFs</p>
            <p className="mt-2 font-display text-sm text-bone-dim">one or many — or click to choose files</p>
            <p className="mt-6 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/70">.pdf // max 50 MB each</p>
          </>
        ) : (
          <>
            <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">// {files.length} thread{files.length === 1 ? "" : "s"} locked in</p>
            <ul className="mx-auto mt-4 max-w-md space-y-1 text-left">
              {files.slice(0, 8).map((f, i) => (<li key={i} className="truncate font-display text-base text-bone-dim">• {f.name}</li>))}
              {files.length > 8 && <li className="font-display text-base text-bone-mute">…and {files.length - 8} more</li>}
            </ul>
          </>
        )}
      </div>
      {has && (
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={props.onClear} className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone">← clear selection</button>
          <button type="button" onClick={props.onParse} className="border border-ember-bright/60 bg-ember/40 px-8 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone transition-colors hover:bg-ember/60 hover:shadow-ember-glow">Release the Eater on {files.length} →</button>
        </div>
      )}
    </div>
  );
}

function Busy({ label, current, index, total }: { label: string; current: string; index: number; total: number }) {
  const pct = Math.round((index / Math.max(1, total)) * 100);
  return (
    <div className="rounded border border-ember-bright/30 bg-iron/70 px-8 py-10 backdrop-blur-sm">
      <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
        <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />{label}
      </p>
      <p className="mt-4 truncate font-display text-lg font-light text-bone">{current}</p>
      <div className="mt-4 h-1 w-full overflow-hidden rounded bg-steel-dark/60"><div className="h-full bg-ember-bright transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function ReviewPanel(props: {
  results: FileResult[]; showFull: boolean; canCommit: boolean;
  onToggleView: () => void; onToggleRedact: (fi: number, mi: number, flagi: number) => void;
  onCommit: () => void; onCancel: () => void;
}) {
  const { results, showFull } = props;
  const ok = results.filter((r) => r.thread);
  const failed = results.filter((r) => r.error);
  const totalMsgs = ok.reduce((n, r) => n + (r.thread?.message_count ?? 0), 0);
  const totalFlags = ok.reduce((n, r) => n + (r.flagCount ?? 0), 0);
  const totalRedactions = ok.reduce((n, r) => n + (r.thread ? redactionCount(r.thread.messages) : 0), 0);
  return (
    <div className="space-y-4">
      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-5 backdrop-blur-sm">
        <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">// batch parsed // review before commit</p>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 font-display text-base font-light uppercase tracking-[0.12em] text-bone-dim md:grid-cols-4">
          <Field label="threads" value={`${ok.length} ok${failed.length ? ` / ${failed.length} failed` : ""}`} />
          <Field label="messages" value={`${totalMsgs}`} />
          <Field label="pii flags" value={`${totalFlags}`} />
          <Field label="will redact" value={`${totalRedactions}`} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">// threads — click a ⚠ flag to redact it</p>
        <button type="button" onClick={props.onToggleView} className="rounded border border-steel-dark bg-iron/60 px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:border-ember-bright/50 hover:bg-ember/20">
          {showFull ? "showing: full record" : "showing: canonical"} ⇄
        </button>
      </div>

      {results.map((r, fi) => <FileCard key={fi} fileIdx={fi} r={r} showFull={showFull} onToggleRedact={props.onToggleRedact} />)}

      <div className="rounded border border-ember-bright/30 bg-ember/10 px-6 py-5 backdrop-blur-sm">
        <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">// commit batch to corpus</p>
        <p className="mt-2 font-display text-sm text-bone-dim">
          {totalRedactions > 0 ? `${totalRedactions} span(s) will be masked in the committed bodies. ` : "No redactions toggled — bodies commit as-is, flags ride along as metadata. "}
          Embeds + writes back to the corpus under the case's defense scope.
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button type="button" onClick={props.onCancel} className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone">← discard & start over</button>
          <button type="button" disabled={!props.canCommit || ok.length === 0} onClick={props.onCommit}
            title={props.canCommit ? "" : "open from a case's Hell Hounds panel to commit"}
            className={`px-8 py-3 font-display text-base font-light uppercase tracking-[0.16em] ${props.canCommit && ok.length ? "border border-ember-bright/60 bg-ember/40 text-bone hover:bg-ember/60 hover:shadow-ember-glow" : "cursor-not-allowed border border-steel-dark bg-iron/40 text-bone-mute opacity-60"}`}>
            Commit {ok.length} to corpus →
          </button>
        </div>
      </div>
    </div>
  );
}

function FileCard({ fileIdx, r, showFull, onToggleRedact }: { fileIdx: number; r: FileResult; showFull: boolean; onToggleRedact: (fi: number, mi: number, flagi: number) => void }) {
  const [open, setOpen] = useState(false);
  if (r.error || !r.thread) {
    return (
      <div className="rounded border border-ember-bright/40 bg-ember/10 px-4 py-3">
        <p className="font-display text-base font-light uppercase tracking-[0.12em] text-ember-bright">⚠ failed // {r.file}</p>
        <p className="mt-1 font-display text-base text-bone-dim">{r.error}</p>
      </div>
    );
  }
  const t = r.thread;
  const shown = t.messages.map((m, mi) => ({ m, mi })).filter(({ m }) => showFull || !m.in_quoted_history);
  return (
    <div className="rounded border border-steel-dark/70 bg-iron/60">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-light text-bone">{t.thread_subject ?? t.source_file}</p>
          <p className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute">
            {t.exhibit_id ? `${t.exhibit_id} // ` : ""}{t.export_route.replace(/_/g, " ")} // {t.message_count} msg{t.message_count === 1 ? "" : "s"}{(r.flagCount ?? 0) > 0 ? ` // ${r.flagCount} PII` : ""}
          </p>
        </div>
        <span className="font-display text-base text-bone-mute">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-steel-dark/50 px-4 py-3">
          {t.warnings.length > 0 && <p className="font-display text-base text-amber-400/80">⚠ {t.warnings.join(" · ")}</p>}
          {shown.map(({ m, mi }) => <MessageRow key={`${m.canonical_message_id}-${mi}`} m={m} onToggleRedact={(flagi) => onToggleRedact(fileIdx, mi, flagi)} />)}
        </div>
      )}
    </div>
  );
}

function MessageRow({ m, onToggleRedact }: { m: EmailMessage; onToggleRedact: (flagi: number) => void }) {
  const sender = m.sender_name ?? m.from_address?.address ?? "(unknown sender)";
  const ts = m.date ?? m.date_raw ?? "(no timestamp)";
  return (
    <div className={`rounded border px-3 py-2 ${m.in_quoted_history ? "border-steel-dark/40 bg-iron-2/20 opacity-70" : "border-steel-dark/70 bg-iron-2/50"}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-bone-mute">
        <span className="font-display text-base font-light uppercase tracking-[0.12em]">
          {ts}
          {m.date_source === "ocr" && <span className="ml-2 rounded border border-amber-500/50 px-1 py-0.5 text-sm text-amber-300">OCR</span>}
          {m.in_quoted_history && <span className="ml-2 rounded border border-steel-dark px-1 py-0.5 text-sm">re-quote</span>}
        </span>
        <span className="font-display text-base font-light uppercase tracking-[0.12em]">{m.header_grammar.replace(/_/g, " ")}</span>
      </div>
      <p className="mt-1 font-display text-sm font-light text-bone">{sender}</p>
      {m.subject && <p className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute/70">{m.subject}</p>}
      <p className="mt-1 line-clamp-2 font-display text-sm leading-relaxed text-bone-dim">{m.body.slice(0, 220)}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {m.attachments.map((a, i) => (<span key={i} className="rounded border border-steel-dark/60 px-1.5 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">📎 {a.filename}{a.ref_only ? " (ref)" : ""}</span>))}
        {m.pii_flags.map((f, i) => (
          <button key={i} type="button" onClick={() => onToggleRedact(i)}
            className={`rounded border px-1.5 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em] ${f.redact ? "border-emerald-500/60 bg-emerald-950/40 text-emerald-300 line-through" : "border-amber-500/50 bg-amber-950/30 text-amber-300 hover:bg-amber-900/40"}`}
            title={f.redact ? "will be masked on commit — click to keep" : "click to redact on commit"}>
            {f.redact ? "redacted" : "⚠"} {f.kind}: {f.preview}
          </button>
        ))}
      </div>
    </div>
  );
}

function CommittedPanel({ outcomes, caseId, onDone, onMore }: { outcomes: CommitOutcome[]; caseId: string | null; onDone: () => void; onMore: () => void }) {
  const ok = outcomes.filter((o) => o.job_id);
  const failed = outcomes.filter((o) => o.error);
  return (
    <div className="space-y-4">
      <div className="rounded border border-ember-bright/30 bg-ember/10 px-6 py-5 backdrop-blur-sm">
        <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">// committed // {ok.length} thread{ok.length === 1 ? "" : "s"} fed to the eater{failed.length ? ` // ${failed.length} failed` : ""}</p>
        <p className="mt-2 font-display text-sm text-bone-dim">Embedding + chunk writes run in the background. They'll appear in the corpus as jobs complete.</p>
        <ul className="mt-3 space-y-1">
          {outcomes.map((o, i) => (
            <li key={i} className="font-display text-base">
              {o.job_id ? <span className="text-emerald-300">✓ {o.file}</span> : <span className="text-ember-bright">✗ {o.file} — {o.error}</span>}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button type="button" onClick={onMore} className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone">← feed more</button>
          <button type="button" onClick={onDone} className="border border-ember-bright/60 bg-ember/40 px-8 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone hover:bg-ember/60 hover:shadow-ember-glow">{caseId ? "Enter the war room →" : "Done →"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (<div><p className="text-bone-mute/70">{label}</p><p className="mt-0.5 truncate text-bone">{value}</p></div>);
}

function ErrorPanel({ message, onReset }: { message: string; onReset: () => void }) {
  return (
    <div className="rounded border border-ember-bright/40 bg-ember/15 px-6 py-6 backdrop-blur-sm">
      <p className="font-display text-base font-light uppercase tracking-[0.18em] text-ember-bright">// something broke</p>
      <p className="mt-3 font-display text-xl font-light text-bone">The Eater coughed.</p>
      <p className="mt-2 font-display text-base text-bone-dim">{message}</p>
      <button type="button" onClick={onReset} className="mt-4 border border-ember-bright/60 bg-iron/70 px-5 py-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:bg-ember/40">Try again →</button>
    </div>
  );
}

export default UploadEvidence;
